import { useSyncExternalStore } from 'react';

/** Mode tema: otomatis mengikuti sistem, atau dipaksa terang/gelap. Disimpan per browser. */
export type ThemeMode = 'auto' | 'light' | 'dark';
const KEY = 'safepos.theme';
const listeners = new Set<() => void>();

const read = (): ThemeMode => {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'auto';
  } catch {
    return 'auto';
  }
};
let mode: ThemeMode = read();

const systemDark = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;

/** Terapkan ke <html data-theme>. */
export function applyTheme(m: ThemeMode = mode): void {
  if (typeof document === 'undefined') return;
  const dark = m === 'dark' || (m === 'auto' && systemDark());
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
}

export function setThemeMode(m: ThemeMode): void {
  mode = m;
  try {
    if (m === 'auto') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, m);
  } catch {
    /* penyimpanan tidak tersedia: berlaku selama sesi */
  }
  applyTheme(m);
  listeners.forEach((l) => l());
}

/** Panggil sekali saat aplikasi mulai (sebelum render) agar tidak berkedip. */
export function initTheme(): void {
  mode = read();
  applyTheme();
  if (typeof matchMedia === 'function')
    matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (mode === 'auto') applyTheme();
    });
}

export function useThemeMode(): ThemeMode {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => mode,
    () => 'auto' as ThemeMode,
  );
}

const NEXT: Record<ThemeMode, ThemeMode> = { auto: 'light', light: 'dark', dark: 'auto' };
const TEXT: Record<ThemeMode, string> = {
  auto: 'Tema: otomatis',
  light: 'Tema: terang',
  dark: 'Tema: gelap',
};
const ICON: Record<ThemeMode, string> = { auto: '◐', light: '☀', dark: '☾' };

/** Tombol ganti tema (otomatis → terang → gelap). */
export function ThemeToggle({ className }: { className?: string }) {
  const m = useThemeMode();
  return (
    <button
      type="button"
      onClick={() => setThemeMode(NEXT[m])}
      aria-label={`${TEXT[m]}. Klik untuk mengganti`}
      title={TEXT[m]}
      className={
        className ??
        'inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg px-3 text-sm text-slate-700 hover:bg-slate-100'
      }
    >
      <span aria-hidden="true">{ICON[m]}</span>
      <span className="ml-1 hidden sm:inline">{TEXT[m].replace('Tema: ', '')}</span>
    </button>
  );
}
